import React, { useCallback, useTransition } from 'react'
import { useIntl } from 'react-intl'

import { type PromotionPlacement } from '@onr/schema'
import { Link } from '@tanstack/react-router'

import { Button } from '@/components/atoms/Button/Button'
import { PROMOTION_SHOP_PLACEMENTS } from '@/components/model/PromotionOption'
import { type RegionPromotionMilestoneDates } from '@/components/model/Region'
import { AdminHeader, type AdminHeaderProps } from '@/components/organisms/AdminHeader/AdminHeader.tsx'
import { PromotionTable, type PromotionTableProps } from '@/components/organisms/PromotionTable/PromotionTable'
import { RegionsMilestonesForm } from '@/components/organisms/RegionMilestonesForm/RegionsMilestonesForm'
import { RegionPromotionTabs } from '@/components/organisms/RegionPromotionTabs'
import { cn } from '@/lib/utils.ts'

export interface RegionPromotionViewProps
  extends AdminHeaderProps, Omit<PromotionTableProps, 'enabledPlacements'>, React.HTMLAttributes<HTMLDivElement> {
  initiativeId: string
  regionId: string
  enabledPlacements: PromotionPlacement[]
  regionPromotionMilestoneDates?: RegionPromotionMilestoneDates
  currentOperatingDate?: string
  onAddPromotionClick?: () => void | Promise<void>
  onMilestoneFormSubmit: (data: RegionPromotionMilestoneDates) => void | Promise<void>
  onDownloadReport?: () => void | Promise<void>
  isDownloadPending?: boolean
}

export const RegionPromotionView = React.forwardRef<HTMLDivElement, RegionPromotionViewProps>(
  (
    {
      className,
      initiativeId,
      regionId,
      regionName,
      promotions = [],
      regionPromotionMilestoneDates,
      currentOperatingDate,
      enabledPlacements,
      onBackToPiloteClicked,
      onAddPromotionClick,
      onMilestoneFormSubmit,
      onDownloadReport,
      isDownloadPending,
      onRowClick,
      ...props
    },
    ref
  ) => {
    const intl = useIntl()
    const [isAddPromotionPending, startAddPromotionTransition] = useTransition()
    const addPromotion = useCallback(() => {
      startAddPromotionTransition(() => onAddPromotionClick?.())
    }, [onAddPromotionClick, startAddPromotionTransition])

    const milestonePromotions = React.useMemo(
      () => promotions.filter((promotion) => promotion.active && promotion.placements.includes('milestone')),
      [promotions]
    )
    const planOptions = intl.formatList(
      PROMOTION_SHOP_PLACEMENTS.filter((placement) => enabledPlacements.includes(placement)).map((placement) =>
        intl.formatMessage({ id: `promotions.form.placements.${placement}` })
      ),
      { type: 'conjunction' }
    )
    const shopLink = (chunks: React.ReactNode[]) => (
      <Link
        key="shop"
        to="/promotions/$initiativeId/$regionId/boutique"
        params={{ initiativeId, regionId }}
        className="font-semibold text-blue-700 hover:text-blue-800">
        {chunks}
      </Link>
    )
    return (
      <div className={cn('flex h-full flex-col', className)} ref={ref} {...props}>
        <AdminHeader regionName={regionName} onBackToPiloteClicked={onBackToPiloteClicked} />

        <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4 pl-16">
          <h1 className="text-xl text-gray-900 select-none">{intl.formatMessage({ id: 'promotions.region.title' })}</h1>

          <RegionPromotionTabs
            initiativeId={initiativeId}
            regionId={regionId}
            activeTab="promotions"
            actions={
              <>
                <Button size="sm" onClick={onDownloadReport} loading={isDownloadPending}>
                  {intl.formatMessage({ id: 'promotions.actions.statistics' })}
                </Button>
                <Button
                  size="sm"
                  disabled={!onAddPromotionClick}
                  onClick={addPromotion}
                  loading={isAddPromotionPending}>
                  {intl.formatMessage({ id: 'promotions.actions.add' })}
                </Button>
              </>
            }
          />

          <p data-testid="promotion-plan-help" className="text-sm text-gray-500">
            {enabledPlacements.length > 0
              ? intl.formatMessage({ id: 'promotions.region.planHelp' }, { options: planOptions, link: shopLink })
              : intl.formatMessage({ id: 'promotions.region.planEmpty' }, { link: shopLink })}
          </p>

          <PromotionTable promotions={promotions} enabledPlacements={enabledPlacements} onRowClick={onRowClick} />

          {milestonePromotions.length > 1 && (
            <RegionsMilestonesForm
              autoSubmit
              values={regionPromotionMilestoneDates}
              promotions={milestonePromotions}
              currentOperatingDate={currentOperatingDate}
              onFormSubmit={onMilestoneFormSubmit}
            />
          )}
        </div>
      </div>
    )
  }
)
